/** ONE SIDEKICK — contextual, permission-aware assistant panel. */
import { createContext, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, X, Send, ExternalLink, HelpCircle, ThumbsUp, ThumbsDown,
  Flag, Loader2, Copy,
} from "lucide-react";

const SidekickCtx = createContext(null);
export const useSidekick = () => useContext(SidekickCtx);

export const SidekickProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState(null); // {etype, eid, title, route}
  const openWith = useCallback((ctx = null) => { setContext(ctx); setOpen(true); }, []);
  return (
    <SidekickCtx.Provider value={{ open, setOpen, context, setContext, openWith }}>
      {children}
      {open && <SidekickPanel />}
    </SidekickCtx.Provider>
  );
};

const SUGGESTIONS = {
  customer: ["Summarise this customer", "What changed recently?", "Show open quotes",
    "Show outstanding orders", "Draft a follow-up", "Next best action"],
  product: ["Summarise this product", "What changed recently?", "Show stock"],
  quote: ["Next best action", "What changed recently?", "Draft a follow-up email"],
  order: ["Next best action", "What changed recently?"],
  customer_po: ["Next best action"],
  none: ["Show Dell laptops older than 90 days", "Show quotes awaiting PO for more than 15 days",
    "Which monthly bank statements are missing?", "Show reserved stock"],
};

function SidekickPanel() {
  const { setOpen, context } = useSidekick();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const ctxKey = context?.eid ? `${context.etype}:${context.eid}` : "global";

  useState(() => {
    api.get("/sidekick/thread", { params: { ctx_key: ctxKey } })
      .then(({ data }) => setMsgs(data.messages || [])).catch(() => {});
  });

  const ask = async (text) => {
    const message = (text || input).trim();
    if (!message) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/sidekick/query", {
        message,
        context: context ? { etype: context.etype, eid: context.eid, title: context.title, route: context.route } : null,
      });
      setMsgs((m) => [...m, { role: "sidekick", message, ...data }]);
    } catch { toast.error("Sidekick could not answer"); }
    finally { setBusy(false); }
  };

  const suggestions = SUGGESTIONS[context?.etype] || SUGGESTIONS.none;

  return (
    <div className="fixed top-0 right-0 bottom-0 z-panel w-full sm:w-[400px] bg-white border-l border-slate-200 shadow-2xl flex flex-col"
      data-testid="sidekick-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-amber-500" />
        <span className="text-sm font-semibold text-slate-900">ONE Sidekick</span>
        <button onClick={() => setOpen(false)} data-testid="sidekick-close"
          className="ml-auto text-slate-400 hover:text-slate-800"><X className="h-4 w-4" /></button>
      </div>
      {context && (
        <div className="px-4 py-2 border-b border-slate-100 bg-amber-50/50 text-[11px] text-slate-600 flex items-center gap-1.5"
          data-testid="sidekick-context-chip">
          <span className="uppercase tracking-wide text-[9px] text-amber-700 font-semibold">{context.etype}</span>
          <span className="truncate">{context.title}</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {msgs.length === 0 && (
          <div className="space-y-1.5">
            <div className="text-[11px] text-slate-400 mb-1">
              I answer only from your live data, within your permissions. Try:
            </div>
            {suggestions.map((s, i) => (
              <button key={s} onClick={() => ask(s)} data-testid={`sidekick-suggestion-${i}`}
                className="block w-full text-left text-[12px] text-slate-700 border border-slate-200 rounded-md px-2.5 py-1.5 hover:bg-amber-50 hover:border-amber-200">
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => m.role === "user"
          ? <div key={i} className="text-[13px] text-white bg-slate-900 rounded-lg rounded-br-sm px-3 py-1.5 w-fit max-w-[85%] ml-auto">{m.text}</div>
          : <Response key={i} r={m} navigate={navigate} />)}
        {busy && <div className="flex items-center gap-2 text-[12px] text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…</div>}
      </div>

      <div className="px-3 py-2.5 border-t border-slate-100 flex items-end gap-1.5">
        <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="Ask about this record or your data…"
          data-testid="sidekick-input"
          className="flex-1 border border-slate-200 rounded-md px-2.5 py-1.5 text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-amber-400" />
        <button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="sidekick-send"
          className="h-8 w-8 grid place-items-center rounded-md bg-slate-900 text-white disabled:opacity-40">
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function Response({ r, navigate }) {
  const [whyOpen, setWhyOpen] = useState(null);
  const fb = async (verdict) => {
    await api.post("/sidekick/feedback", { message: r.message, response_kind: r.kind, verdict });
    toast.success("Thanks for the feedback");
  };
  return (
    <div className="border border-slate-200 rounded-lg rounded-bl-sm px-3 py-2.5 mr-6 text-[13px] space-y-2" data-testid="sidekick-response">
      {r.kind === "text" && <div className="text-slate-700">{r.text}</div>}
      {r.kind === "denied" && <div className="text-rose-700 text-[12px]" data-testid="sidekick-denied">{r.text}</div>}

      {r.kind === "summary" && r.summary && (
        <div className="space-y-2">
          {[["Summary", [r.summary.summary]], ["What changed", r.summary.what_changed],
            ["Needs attention", r.summary.needs_attention], ["Next actions", r.summary.next_actions]].map(([h, lines]) => (
            <div key={h}>
              <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">{h}</div>
              {(lines || []).map((l, i) => <div key={i} className="text-slate-700 text-[12px]">• {l}</div>)}
            </div>
          ))}
        </div>
      )}

      {r.understood_as && (
        <div data-testid="sidekick-understood">
          <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-1">Understood as</div>
          <div className="flex flex-wrap gap-1">
            {r.understood_as.map((u, i) => (
              <span key={i} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                {u.field} = <span className="font-medium">{u.value}</span>
              </span>
            ))}
            {r.count != null && <span className="text-[10px] text-slate-400 ml-1">{r.count} match(es)</span>}
          </div>
        </div>
      )}

      {(r.items || []).map((it, i) => (
        <button key={i} onClick={() => it.route && navigate(it.route)}
          className="w-full text-left flex items-center gap-2 border border-slate-100 rounded px-2 py-1 hover:bg-slate-50">
          <span className="text-[12px] text-slate-800 truncate">{it.title}</span>
          {it.subtitle && <span className="text-[11px] text-slate-400 truncate ml-auto">{it.subtitle}</span>}
          {it.route && <ExternalLink className="h-3 w-3 text-slate-300 shrink-0" />}
        </button>
      ))}

      {(r.nba || []).map((n, i) => (
        <div key={i} className="border border-amber-200 bg-amber-50/50 rounded px-2.5 py-2" data-testid={`sidekick-nba-${i}`}>
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium text-slate-900">{n.action}</span>
            {n.confirm && <span className="text-[9px] uppercase text-amber-700 border border-amber-300 rounded px-1 whitespace-nowrap shrink-0">needs confirmation</span>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            {n.route && (
              <button onClick={() => navigate(n.route)} data-testid={`sidekick-nba-go-${i}`}
                className="text-[11px] bg-slate-900 text-white rounded px-2 py-0.5">Open</button>
            )}
            <button onClick={() => setWhyOpen(whyOpen === i ? null : i)} data-testid={`sidekick-nba-why-${i}`}
              className="text-[11px] text-slate-500 inline-flex items-center gap-0.5 hover:text-slate-800">
              <HelpCircle className="h-3 w-3" /> Why?
            </button>
          </div>
          {whyOpen === i && <div className="text-[11px] text-slate-600 mt-1.5 bg-white border border-slate-100 rounded px-2 py-1.5" data-testid={`sidekick-why-text-${i}`}>{n.why}</div>}
        </div>
      ))}
      {r.kind === "nba" && (r.nba || []).length === 0 && (
        <div className="text-[12px] text-slate-500">No recommended action — this record's workflow is up to date.</div>
      )}

      {r.kind === "draft" && r.draft && (
        <div className="space-y-1.5" data-testid="sidekick-draft">
          <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Draft — review before sending</div>
          <div className="text-[11px] text-slate-500">To: {r.draft.to || "—"} · {r.draft.subject}</div>
          <pre className="text-[11px] text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded p-2 max-h-44 overflow-y-auto">{r.draft.body}</pre>
          <button onClick={() => { navigator.clipboard.writeText(r.draft.body); toast.success("Draft copied"); }}
            data-testid="sidekick-draft-copy"
            className="text-[11px] border border-slate-200 rounded px-2 py-1 inline-flex items-center gap-1 hover:bg-slate-50">
            <Copy className="h-3 w-3" /> Copy draft
          </button>
          {r.note && <div className="text-[10px] text-amber-700">{r.note}</div>}
        </div>
      )}

      {(r.sources || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {r.sources.map((s, i) => (
            <button key={i} onClick={() => navigate(s.route)} data-testid={`sidekick-source-${i}`}
              className="text-[10px] text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1 hover:bg-slate-50">
              <ExternalLink className="h-2.5 w-2.5" /> {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 pt-0.5 border-t border-slate-50">
        <button onClick={() => fb("helpful")} title="Helpful" data-testid="sidekick-fb-helpful"
          className="text-slate-300 hover:text-emerald-600"><ThumbsUp className="h-3 w-3" /></button>
        <button onClick={() => fb("not_helpful")} title="Not helpful" data-testid="sidekick-fb-nothelpful"
          className="text-slate-300 hover:text-rose-600"><ThumbsDown className="h-3 w-3" /></button>
        <button onClick={() => fb("incorrect")} title="Report incorrect" data-testid="sidekick-fb-incorrect"
          className="text-slate-300 hover:text-amber-600"><Flag className="h-3 w-3" /></button>
      </div>
    </div>
  );
}

export const SidekickTrigger = () => {
  const sk = useSidekick();
  if (!sk) return null;
  return (
    <button onClick={() => sk.openWith(null)} data-testid="one-bar-sidekick" title="ONE Sidekick"
      className="h-9 w-9 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] transition-colors">
      <Sparkles className="h-[18px] w-[18px]" strokeWidth={1.6} />
    </button>
  );
};

export default SidekickPanel;
